// Adjust the path as necessary
import { tsRestClient } from "@api/tsRest";

import { GetChatRequestType } from "@interfaces/chat.type";
import { SearchType } from "@interfaces/ipsearch.type";

export const companySearch = async (queryString: string, preferredIndustries?: string[]) => {
  const { body, status } = await tsRestClient.ipSearch.getRecommendedCompanies({
    body: { preferredIndustries, queryString },
  });

  if (status !== 200) {
    throw new Error("Failed to get companies from Smart Search");
  }

  return body;
};

export const loadCompanySearch = async (searchId: string): Promise<SearchType> => {
  const { body, status } = await tsRestClient.ipSearch.getSearch({ params: { searchId } });
  if (status !== 200) {
    throw new Error("Failed to get companies from Smart Search");
  }
  return body;
};

export const getInsight = async (
  searchId: string,
  companyId: string,
  model: GetChatRequestType["model"],
  messages?: string[],
) => {
  const { body, status } = await tsRestClient.ipSearch.getInsight({
    body: { companyId, messages, model },
    params: { searchId },
  });
  if (status !== 200) {
    throw new Error("Failed to get insight");
  }
  return body as unknown as SearchType;
};

export const clearSearch = async () => {
  await tsRestClient.ipSearch.clearSearch();
};

// SSE version for long-running company search
export const companySearchSSE = (
  queryString: string,
  preferredIndustries?: string[],
  onProgress?: (message: string) => void,
): Promise<SearchType> => {
  return new Promise((resolve, reject) => {
    // Get auth token from localStorage or your auth system
    const token = localStorage.getItem("authToken");

    // Build query string
    const parameters = new URLSearchParams();
    parameters.append("queryString", queryString);
    if (preferredIndustries && preferredIndustries.length > 0) {
      parameters.append("preferredIndustries", preferredIndustries.join(","));
    }

    // Create EventSource with query parameters (EventSource only supports GET)
    const apiUrl = process.env.REACT_APP_API_URL || globalThis.location.origin;

    const eventSource = new EventSource(
      `${apiUrl}/v2/ip-search/recommended-companies-sse?${parameters.toString()}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        withCredentials: true,
      } as any, // EventSource doesn't have official header support but some polyfills do
    );

    let heartbeatCount = 0;

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        switch (data.type) {
          case "complete": {
            console.log("SSE Complete");
            eventSource.close();
            resolve(data.result);
            break;
          }

          case "connected": {
            console.log("SSE Connected:", data.message);
            onProgress?.("Connected to server, processing...");
            break;
          }

          case "error": {
            console.error("SSE Error:", data.message);
            eventSource.close();
            reject(new Error(data.message));
            break;
          }

          case "heartbeat": {
            heartbeatCount++;
            console.log(`SSE Heartbeat #${heartbeatCount}`);
            onProgress?.(`Processing... (${heartbeatCount * 30}s elapsed)`);
            break;
          }

          default: {
            console.log("Unknown SSE message type:", data);
          }
        }
      } catch (error) {
        console.error("Failed to parse SSE message:", event.data, error);
      }
    };

    eventSource.onerror = (error) => {
      console.error("SSE Connection error:", error);
      eventSource.close();
      reject(new Error("Connection to server lost. Please try again."));
    };

    // Cleanup function if needed
    // You might want to return a cancel function
    // return () => eventSource.close();
  });
};
